import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  completePublicPdfReturn,
  downloadPublicPdf,
  getPublicPdfReturn,
} from '../../api/training';

function getFilenameFromDisposition(dispositionHeader) {
  if (!dispositionHeader) {
    return 'Stundenprotokoll.pdf';
  }

  const filenameMatch = dispositionHeader.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || 'Stundenprotokoll.pdf';
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString('de-DE');
}

function PublicPdfReturnPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [context, setContext] = useState(null);
  const [state, setState] = useState('open');
  const [expiresAt, setExpiresAt] = useState('');

  const [actorName, setActorName] = useState('');
  const [completionNote, setCompletionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState(false);

  const canSubmit = useMemo(() => {
    return !loading && state === 'open' && actorName.trim().length > 0 && !submitting;
  }, [actorName, loading, state, submitting]);

  const loadContext = useCallback(async () => {
    if (!token) {
      setLoadError('Ungültiger Rückläufer-Link.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');

    try {
      const response = await getPublicPdfReturn(token);
      setContext(response.data?.context || null);
      setState(response.data?.state || 'open');
      setExpiresAt(response.data?.expiresAt || '');
    } catch (error) {
      setContext(null);
      setState('invalid');
      setLoadError(error.response?.data?.error || 'Rückläufer-Link konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const handleDownloadPdf = async () => {
    if (!token) {
      return;
    }

    try {
      const response = await downloadPublicPdf(token);

      const filename = getFilenameFromDisposition(response.headers?.['content-disposition']);
      triggerBlobDownload(response.data, filename);
    } catch (error) {
      setSubmitError(error.response?.data?.error || 'PDF konnte nicht heruntergeladen werden.');
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || !token) {
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');
    setHasSubmittedSuccessfully(false);

    try {
      const response = await completePublicPdfReturn(token, {
        actorName: actorName.trim(),
        completionNote: completionNote.trim() || null,
      });

      setSubmitSuccess(response.data?.message || 'Rückläufer wurde bestätigt.');
      setHasSubmittedSuccessfully(true);
      setState('used');
      await loadContext();
    } catch (error) {
      setSubmitError(error.response?.data?.error || 'Rückläufer konnte nicht bestätigt werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={2.5} sx={{ maxWidth: 760, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Rückläuferbestätigung
        </Typography>

        {loading && (
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Link wird geprüft...</Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {loadError && !loading && (
          <Alert severity="error">{loadError}</Alert>
        )}

        {!loading && context && (
          <Card>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Protokollinformationen
                </Typography>
                <Typography variant="body2"><strong>Datum:</strong> {context.date || '-'}</Typography>
                <Typography variant="body2"><strong>Art:</strong> {context.entryType || '-'}</Typography>
                <Typography variant="body2"><strong>Verantwortlich:</strong> {context.responsibleName || '-'}</Typography>
                <Typography variant="body2"><strong>Zeitraum:</strong> {context.timeRange || '-'}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {context.completionStatus || '-'}</Typography>
                <Typography variant="body2"><strong>Bestätigt durch:</strong> {context.completionBy || '-'}</Typography>
                <Typography variant="body2"><strong>Bestätigt am:</strong> {context.completionAt || '-'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Link gültig bis: {formatDate(expiresAt)}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {!loading && state === 'open' && (
          <Card>
            <CardContent>
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Rückläufer bestätigen
                </Typography>
                <TextField
                  fullWidth
                  required
                  label="Name der bestätigenden Person"
                  value={actorName}
                  onChange={(event) => setActorName(event.target.value)}
                  disabled={submitting}
                />
                <TextField
                  fullWidth
                  label="Bemerkung (optional)"
                  value={completionNote}
                  onChange={(event) => setCompletionNote(event.target.value)}
                  disabled={submitting}
                  multiline
                  minRows={2}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" onClick={handleDownloadPdf} disabled={submitting}>
                    PDF herunterladen
                  </Button>
                  <Button variant="contained" color="success" onClick={handleSubmit} disabled={!canSubmit}>
                    {submitting ? 'Speichert...' : 'Rückläufer bestätigen'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {!loading && state === 'used' && !hasSubmittedSuccessfully && (
          <Alert severity="info">
            Dieser Rückläufer wurde bereits bestätigt. Eine erneute Bestätigung ist nicht möglich.
          </Alert>
        )}

        {submitError && <Alert severity="error">{submitError}</Alert>}
        {submitSuccess && <Alert severity="success">{submitSuccess}</Alert>}
      </Stack>
    </Box>
  );
}

export default PublicPdfReturnPage;
